import { useMemo, useState } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Collapse, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { FormikProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { switchMap, tap } from 'rxjs/operators';
import type { IInvoiceItem } from 'v2/components/Pages/Orders/interfaces';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import ordersService from 'v2/services/orders';

import { Icon } from '@eduzz/ui-antd-theme';

const Tracking = () => {
  const { message } = App.useApp();
  // TODO: migrate to react-hook-form
  const [isSendingTrackingCode, setIsSendingTrackingCode] = useState<boolean>(false);
  const [isLoading] = useObservable(() => ordersService.isLoadingInvoiceItems(), []);
  const [invoiceItems] = useObservable(() => {
    return ordersService.getInvoiceItems();
  }, []);

  const [onSendDeliveryCode] = useCallbackObservable(
    (values: IInvoiceItem, form: FormikProps<IInvoiceItem>) => {
      setIsSendingTrackingCode(true);
      return ordersService.sendDeliveryCodeInvoiceItem(values).pipe(
        tap(() => {
          try {
            form.setSubmitting(false);
            message.success('Código de rastreio cadastrado com sucesso');
          } catch (error) {
            setIsSendingTrackingCode(false);
            message.error(typeof error === 'string' ? error : 'Não foi possível cadastrar o código de rastreio.');
          }
        }),
        switchMap(() => ordersService.loadInvoiceItems()),
        tap(() => setIsSendingTrackingCode(false))
      );
    },
    [message]
  );

  const columns = useMemo(
    (): ColumnsType<IInvoiceItem> => [
      {
        key: 'content',
        render: (_, item) => (
          <>
            {item.id} -{' '}
            <Typography.Text>
              <Truncate final='.' count={10}>
                {item.description}
              </Truncate>
            </Typography.Text>
          </>
        )
      },
      {
        key: 'tracking',
        render: (_, item) => (
          <Formik initialValues={item} onSubmit={onSendDeliveryCode}>
            {({ values, isSubmitting }: FormikProps<IInvoiceItem>) => {
              return (
                <Form>
                  <Field
                    placeholder='Código'
                    id='order.traking_code'
                    name='order.traking_code'
                    value={values.order.traking_code ? values.order.traking_code : null}
                    disabled={Boolean(item.order.traking_code)}
                    component={TextFormField}
                    suffix={
                      isSendingTrackingCode ? (
                        <Icon.LoaderCircle className='animate-spin' size={16} />
                      ) : (
                        <Button
                          disabled={
                            isSubmitting ||
                            !values.order.traking_code ||
                            item.order.traking_code === values.order.traking_code
                          }
                          htmlType='submit'
                          type='text'
                          size='small'
                          aria-label='Enviar código de rastreio'
                        >
                          ok
                        </Button>
                      )
                    }
                  />
                </Form>
              );
            }}
          </Formik>
        )
      }
    ],
    [onSendDeliveryCode, isSendingTrackingCode]
  );

  return (
    <PermissionHide action='myeduzz:minhas-entregas:editar'>
      <Collapse>
        <Collapse.Panel key='tracking' header='Rastreio' className='[&_.ant-collapse-content-box]:p-0!'>
          <p className='px-6 pt-6 pb-2 text-[15px] text-[#f50057]'>
            Ao enviar o código de rastreio o status da entrega será atualizado para <b>ENVIADO</b> automaticamente.
          </p>
          <Table
            columns={columns}
            dataSource={Array.isArray(invoiceItems) ? invoiceItems : []}
            rowKey='id'
            loading={isLoading}
            pagination={false}
            showHeader={false}
            locale={{ emptyText: 'Nenhum item encontrado...' }}
            scroll={{ x: 'max-content' }}
          />
        </Collapse.Panel>
      </Collapse>
    </PermissionHide>
  );
};
export default Tracking;
